import { Routes } from '@angular/router';
import { adminGuard } from '@cnt-admin/data-access';
import { AdminAuthRedirectComponent, AdminLayoutComponent, AdminLoginComponent } from '@cnt-admin/ui';

/**
 * `listings/` and `properties/` (same children, both URLs are linked from
 * elsewhere): the listing table, listing + site editors (`edit/:id…`) and the
 * boondock editor (`boondocking/…`), each lazy-loaded. The table must come
 * first: with no segments left the router accepts the first empty-path lazy
 * sibling even if none of its children match, while for `edit/…` the table's
 * single `''` leaf can't consume the segments, so the router moves on.
 */
const LISTING_CHILDREN: Routes = [
  { path: '', loadChildren: () => import('@cnt-admin/feature-listings').then((m) => m.ADMIN_LISTINGS_ROUTES) },
  { path: '', loadChildren: () => import('@cnt-admin/listing-editor').then((m) => m.ADMIN_LISTING_EDITOR_ROUTES) },
  { path: '', loadChildren: () => import('@cnt-admin/feature-boondock').then((m) => m.ADMIN_BOONDOCK_EDITOR_ROUTES) },
];

export const appRoutes: Routes = [
  {
    // Home: "Needs attention". The listings table lives at sites/, listings/ and properties/.
    path: '',
    pathMatch: 'full',
    component: AdminLayoutComponent,
    canActivate: [adminGuard],
    runGuardsAndResolvers: 'always',
    loadChildren: () => import('@cnt-admin/feature-tools').then((m) => m.ADMIN_HOME_ROUTES),
  },
  {
    path: 'sites',
    component: AdminLayoutComponent,
    canActivate: [adminGuard],
    runGuardsAndResolvers: 'always',
    children: [
      {
        path: 'boondock',
        loadChildren: () => import('@cnt-admin/feature-boondock').then((m) => m.ADMIN_BOONDOCK_LIST_ROUTES),
      },
      {
        path: '',
        loadChildren: () => import('@cnt-admin/feature-listings').then((m) => m.ADMIN_LISTINGS_ROUTES),
      },
    ],
  },
  {
    path: 'reviews',
    component: AdminLayoutComponent,
    canActivate: [adminGuard],
    loadChildren: () => import('@cnt-admin/feature-listings').then((m) => m.ADMIN_REVIEWS_ROUTES),
  },
  {
    path: 'quality',
    component: AdminLayoutComponent,
    canActivate: [adminGuard],
    loadChildren: () => import('@cnt-admin/feature-listings').then((m) => m.ADMIN_LISTING_QUALITY_ROUTES),
  },
  {
    path: 'unfinished',
    component: AdminLayoutComponent,
    canActivate: [adminGuard],
    loadChildren: () => import('@cnt-admin/feature-listings').then((m) => m.ADMIN_UNFINISHED_LISTINGS_ROUTES),
  },
  {
    path: 'nudge',
    component: AdminLayoutComponent,
    canActivate: [adminGuard],
    loadChildren: () => import('@cnt-admin/feature-tools').then((m) => m.ADMIN_NUDGE_ROUTES),
  },
  {
    path: 'pois',
    component: AdminLayoutComponent,
    canActivate: [adminGuard],
    runGuardsAndResolvers: 'always',
    loadChildren: () => import('@cnt-admin/feature-pois').then((m) => m.ADMIN_POIS_ROUTES),
  },
  {
    path: 'reservations',
    component: AdminLayoutComponent,
    canActivate: [adminGuard],
    runGuardsAndResolvers: 'always',
    loadChildren: () => import('@cnt-admin/feature-reservations').then((m) => m.ADMIN_RESERVATIONS_ROUTES),
  },
  {
    path: 'support',
    component: AdminLayoutComponent,
    canActivate: [adminGuard],
    runGuardsAndResolvers: 'always',
    loadChildren: () => import('@cnt-admin/feature-support').then((m) => m.ADMIN_SUPPORT_ROUTES),
  },
  {
    path: 'users',
    component: AdminLayoutComponent,
    canActivate: [adminGuard],
    runGuardsAndResolvers: 'always',
    loadChildren: () => import('@cnt-admin/feature-users').then((m) => m.ADMIN_USERS_ROUTES),
  },
  {
    path: 'auth',
    children: [
      {
        path: 'login',
        component: AdminLoginComponent,
      },
      // Cognito Hosted UI callback for Google / Facebook / Apple (environment.cognitoCallback).
      { path: 'redirect', component: AdminAuthRedirectComponent },
      { path: '**', redirectTo: 'login' },
    ],
  },
  {
    path: 'listings',
    component: AdminLayoutComponent,
    canActivate: [adminGuard],
    runGuardsAndResolvers: 'always',
    children: LISTING_CHILDREN,
  },
  {
    path: 'properties',
    component: AdminLayoutComponent,
    canActivate: [adminGuard],
    runGuardsAndResolvers: 'always',
    children: LISTING_CHILDREN,
  },
  {
    path: 'email-template',
    component: AdminLayoutComponent,
    canActivate: [adminGuard],
    runGuardsAndResolvers: 'always',
    loadChildren: () => import('@cnt-admin/feature-tools').then((m) => m.ADMIN_EMAIL_ROUTES),
  },
  {
    path: 'affiliates',
    component: AdminLayoutComponent,
    canActivate: [adminGuard],
    loadChildren: () => import('@cnt-admin/feature-affiliates').then((m) => m.ADMIN_AFFILIATES_ROUTES),
  },
  {
    path: 'rating',
    component: AdminLayoutComponent,
    canActivate: [adminGuard],
    loadChildren: () => import('@cnt-admin/feature-pois').then((m) => m.ADMIN_CARETAKER_ROUTES),
  },
  {
    path: 'log',
    component: AdminLayoutComponent,
    canActivate: [adminGuard],
    loadChildren: () => import('@cnt-admin/feature-tools').then((m) => m.ADMIN_LOG_ROUTES),
  },
  {
    path: 'search',
    component: AdminLayoutComponent,
    canActivate: [adminGuard],
    loadChildren: () => import('@cnt-admin/feature-tools').then((m) => m.ADMIN_SEARCH_ROUTES),
  },
  {
    path: 'analytics',
    component: AdminLayoutComponent,
    canActivate: [adminGuard],
    loadChildren: () => import('@cnt-admin/feature-tools').then((m) => m.ADMIN_ANALYTICS_ROUTES),
  },
  {
    path: 'reports',
    component: AdminLayoutComponent,
    canActivate: [adminGuard],
    loadChildren: () => import('@cnt-admin/feature-tools').then((m) => m.ADMIN_REPORTS_ROUTES),
  },
  {
    path: 'articles',
    component: AdminLayoutComponent,
    canActivate: [adminGuard],
    runGuardsAndResolvers: 'always',
    loadChildren: () => import('@cnt-admin/feature-articles').then((m) => m.ADMIN_ARTICLES_ROUTES),
  },
  {
    path: 'article-builder',
    component: AdminLayoutComponent,
    canActivate: [adminGuard],
    runGuardsAndResolvers: 'always',
    loadChildren: () => import('@cnt-admin/feature-articles').then((m) => m.ADMIN_ARTICLE_BUILDER_ROUTES),
  },
  {
    path: 'payouts',
    component: AdminLayoutComponent,
    canActivate: [adminGuard],
    runGuardsAndResolvers: 'always',
    loadChildren: () => import('@cnt-admin/feature-users').then((m) => m.ADMIN_PAYOUTS_ROUTES),
  },
  { path: '**', redirectTo: '/' },
];
